<template>
  <div
    id="business_documents"
    class="flex flex-col gap-6 text-[13px] secondary_color proxima_nova"
  >
    <div id="business_address" class="flex flex-col gap-2">
      <div class="pb-3">
        <span class="text-[19px] font-semibold text_color">
          {{ currentDocumentConfig.sectionTitle }}
        </span>
      </div>

      <span class="text-sm">Document type*</span>

      <a-select
        v-model:value="localFormData.addressDocumentType"
        class="w-full"
        placeholder="Choose a document type"
      >
        <template #suffixIcon>
          <ThemeImage
            class="size-4 opacity-40"
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_white_arrow"
          />
        </template>

        <a-select-option
          v-for="(type, index) in currentDocumentConfig.addressDocumentTypes"
          :key="`address_type_${index}`"
          :value="type.value"
        >
          {{ type.label }}
        </a-select-option>
      </a-select>

      <div class="w-fit">
        <AttachedItem
          v-if="addressFileObject"
          :file="addressFileObject"
          :downloadable="false"
          @remove="() => removeFile('address')"
        />

        <AttachmentButton
          v-else
          source="supporting_documents_address"
          label="Upload Document"
          :multiple="false"
          :variant="variants.VARIANT1"
          :type="attachmentTypes.ARROW"
          @file-selected="(files) => handleFileUpload(files, 'address')"
        />
      </div>
    </div>

    <div
      v-if="currentDocumentConfig.showBothSections"
      id="business_name"
      class="flex flex-col gap-2"
    >
      <div class="pb-3">
        <span class="text-[19px] font-semibold text_color">
          Business Name
        </span>
      </div>

      <span class="text-sm">Document type*</span>

      <a-select
        v-model:value="localFormData.nameDocumentType"
        class="w-full"
        placeholder="Choose a document type"
      >
        <template #suffixIcon>
          <ThemeImage
            class="size-4 opacity-40"
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_white_arrow"
          />
        </template>
        <a-select-option
          v-for="(type, index) in currentDocumentConfig.nameDocumentTypes"
          :key="`name_type_${index}`"
          :value="type.value"
        >
          {{ type.label }}
        </a-select-option>
      </a-select>

      <div class="w-fit">
        <AttachedItem
          v-if="nameFileObject"
          :file="nameFileObject"
          :downloadable="false"
          @remove="() => removeFile('name')"
        />

        <AttachmentButton
          v-else
          source="supporting_documents_name"
          label="Upload Document"
          :multiple="false"
          :variant="variants.VARIANT1"
          :type="attachmentTypes.ARROW"
          @file-selected="(files) => handleFileUpload(files, 'name')"
        />
      </div>
    </div>

    <a-button
      class="primary_medium self-end mt-6"
      :disabled="!isStepValid"
      :loading="submitting"
      @click="handleContinue"
    >
      {{ submitting ? "Submitting" : "Submit" }}
    </a-button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { message } from "ant-design-vue";
import AttachmentButton from "@/src/components/attachment_components/AttachmentButton.vue";
import AttachedItem from "@/src/components/attachment_components/AttachedItem.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  addressDocumentTypes,
  nameDocumentTypes,
  countryMapping,
  getCountryDocumentConfig,
} from "@/src/constants/phoneNumberConstants.js";
import CallService from "@/src/api/CallService.js";
import {
  maxFileSizes,
  variants,
} from "@/src/constants/customComponentConstants";
import { attachmentTypes } from "@/src/constants/attachmentConstants.js";

const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

const props = defineProps({
  formData: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["update:formData", "next-step"]);

const router = useRouter();
const route = useRoute();
const localFormData = ref({ ...props.formData });
const submitting = ref(false);

const countryCode = computed(() => route?.params?.country?.toUpperCase() || "");

const currentDocumentConfig = computed(() => {
  return getCountryDocumentConfig(countryCode.value);
});

const isStepValid = computed(() => {
  if (currentDocumentConfig.value.showBothSections) {
    return Boolean(
      localFormData.value.addressDocumentType &&
        localFormData.value.addressFile &&
        localFormData.value.nameDocumentType &&
        localFormData.value.nameFile,
    );
  } else {
    return Boolean(
      localFormData.value.addressDocumentType &&
        localFormData.value.addressFile,
    );
  }
});

const addressFileObject = computed(() => {
  const file = localFormData.value.addressFile;
  return file
    ? {
        id: "address",
        file_name: file.name,
        file_size: file.size,
      }
    : null;
});

const nameFileObject = computed(() => {
  const file = localFormData.value.nameFile;
  return file
    ? {
        id: "name",
        file_name: file.name,
        file_size: file.size,
      }
    : null;
});

const handleFileUpload = (files, type) => {
  const file = files?.[0];
  if (!file) return;

  if (file.size > maxFileSizes.document) {
    message.error("File must be smaller than 5MB");
    return;
  }

  if (!allowedTypes.includes(file.type)) {
    message.error("Only PDF, JPEG, or PNG files are allowed");
    return;
  }

  if (type === "address") {
    localFormData.value.addressFile = file;
  } else if (type === "name") {
    localFormData.value.nameFile = file;
  }
};

const removeFile = (type) => {
  if (type === "address") {
    localFormData.value.addressFile = null;
  } else if (type === "name") {
    localFormData.value.nameFile = null;
  }
};

const toCreateNzRegulatoryBundle = async () => {
  try {
    submitting.value = true;

    const formData = new FormData();
    formData.append("id", localFormData.value?.infoID);
    formData.append("address_type", localFormData.value.addressDocumentType);
    formData.append("name_type", localFormData.value.nameDocumentType);
    formData.append("address_file", localFormData.value.addressFile);
    formData.append("name_file", localFormData.value.nameFile);
    formData.append("country_code", countryCode.value);

    const response = await CallService.createNzRegulatoryBundle(formData);
    if (response?.success) {
      message.success("Documents submitted successfully!");
      await router.push("/settings#phone_numbers");
      return true;
    }
  } catch (error) {
    console.error("Document submission failed:", error);
    message.error("Failed to submit documents");
    return false;
  } finally {
    submitting.value = false;
  }
};

const toCreateChRegulatoryBundle = async () => {
  try {
    submitting.value = true;

    const formData = new FormData();
    formData.append("id", localFormData.value?.infoID);
    formData.append("country_code", countryCode.value);

    if (currentDocumentConfig.value.apiPayloadType === "single_file") {
      formData.append("document_type", localFormData.value.addressDocumentType);
      formData.append(
        "business_register_file",
        localFormData.value.addressFile,
      );
    } else {
      formData.append("address_type", localFormData.value.addressDocumentType);
      formData.append("name_type", localFormData.value.nameDocumentType);
      formData.append("address_file", localFormData.value.addressFile);
      formData.append("name_file", localFormData.value.nameFile);
    }

    const response = await CallService.createChRegulatoryBundle(formData);
    if (response?.success) {
      message.success("Documents submitted successfully!");
      await router.push("/settings#phone_numbers");
      return true;
    }
  } catch (error) {
    console.error("Document submission failed:", error);
    message.error("Failed to submit documents");
    return false;
  } finally {
    submitting.value = false;
  }
};

const handleContinue = async () => {
  emit("update:formData", { ...localFormData.value });

  let success = false;
  if (countryCode.value === countryMapping.NZ) {
    success = await toCreateNzRegulatoryBundle();
  } else if (countryCode.value === countryMapping.CH) {
    success = await toCreateChRegulatoryBundle();
  } else {
    message.error(`Unsupported country: ${countryCode.value}`);
    return;
  }

  if (success) emit("next-step");
};

watch(
  () => props.formData,
  (newVal) => {
    localFormData.value = { ...newVal };
  },
  { deep: true },
);
</script>
